import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { FileText, ArrowRight } from 'lucide-react'
import { listTasks } from '@/api/zuzahlerTasks'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

/**
 * Phase H — Inbox des eingeloggten Zuzahler-AP.
 *
 * Route: /zuzahler/tasks
 * Auth:  jeder eingeloggte Portal-User (BFF filtert nach Email + UserId).
 *
 * Zeigt:
 *  - alle KUs wo `zuzahler_ap_email == user.email` und Status ∈ {sent, in_progress}
 *  - PLUS KUs wo `signed_by_user_id == user.id` und Status ∈ {sent, in_progress, signed}
 *
 * Klick auf eine Zeile -> TaskDetailPage.
 */
export default function TasksListPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zuzahlerTasks'],
    queryFn: listTasks,
  })

  const items = data?.items ?? []

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5">
        <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <FileText size={20} className="text-fom-500" />
          Kostenübernahme-Aufgaben
        </h1>
        <p className="text-gray-500 text-[0.78rem] mt-0.5">
          Erklärungen zur Kostentragung, die für Sie zur digitalen Bestätigung vorliegen.
        </p>
      </div>

      {isLoading && <Spinner className="mt-10" />}
      {error && <ErrorAlert message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && items.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-500 text-sm">
          Keine offenen Aufgaben.
        </div>
      )}

      {!isLoading && !error && items.length > 0 && (
        <div className="space-y-3">
          {items.map((k) => {
            const studiName = [k.StudiVorname, k.StudiNachname].filter(Boolean).join(' ')
            const created = new Date(k.CreatedAt).toLocaleDateString('de-DE')
            return (
              <Link
                key={k.Id}
                to={`/zuzahler/tasks/${k.Id}`}
                className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-fom-500 hover:shadow-sm transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      {studiName || '–'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {k.StudiStudienort && (
                        <span>Studienort {k.StudiStudienort}</span>
                      )}
                      {k.StudiMatrikel && (
                        <span className="ml-2 text-gray-400">
                          · Matrikel <span className="font-mono">{k.StudiMatrikel}</span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">Erstellt am {created}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusBadge status={k.Status} />
                    <ArrowRight size={18} className="text-gray-300" />
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; classes: string }> = {
    sent: {
      label: 'Offen',
      classes: 'bg-amber-50 border-amber-200 text-amber-800',
    },
    in_progress: {
      label: 'In Bearbeitung',
      classes: 'bg-blue-50 border-blue-200 text-blue-800',
    },
    signed: {
      label: 'Signiert',
      classes: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    },
  }
  const m = map[status] ?? {
    label: status,
    classes: 'bg-gray-50 border-gray-200 text-gray-700',
  }
  return (
    <span
      className={`text-[0.7rem] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${m.classes}`}
    >
      {m.label}
    </span>
  )
}
